import { WebSocketMessage, parseWebSocketMessage } from '../types';
import { API_CONFIG } from '../config';
import { fetchAuthSession } from 'aws-amplify/auth';

export type ConnectionState = 'connected' | 'reconnecting' | 'disconnected' | 'fallback';

type MessageHandler = (message: WebSocketMessage) => void;
type ConnectionStateHandler = (state: ConnectionState) => void;

export class WebSocketManager {
  private ws: WebSocket | null = null;
  private messageHandlers: Set<MessageHandler> = new Set();
  private connectionStateHandlers: Set<ConnectionStateHandler> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private baseReconnectDelay = 1000; // Start with 1 second
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private isIntentionallyClosed = false;
  private authToken: string | null = null;
  private connectionState: ConnectionState = 'disconnected';
  private static PING_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes — keeps connection alive (API GW idle timeout is 10 min)

  constructor() {
    this.connect = this.connect.bind(this);
    this.disconnect = this.disconnect.bind(this);
    this.send = this.send.bind(this);
  }

  /**
   * Connect to WebSocket server with authentication token
   */
  connect(authToken: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    this.authToken = authToken;
    this.isIntentionallyClosed = false;

    try {
      const wsUrl = `${API_CONFIG.WEBSOCKET_URL}?token=${encodeURIComponent(authToken)}`;
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from WebSocket server intentionally
   */
  disconnect(): void {
    this.isIntentionallyClosed = true;
    this.reconnectAttempts = 0;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.stopPing();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.setConnectionState('disconnected');
  }

  /**
   * Send a message through the WebSocket
   */
  send(message: unknown): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.error('WebSocket is not connected');
    }
  }

  /**
   * Register a message handler
   */
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  /**
   * Register a connection state handler
   */
  onConnectionState(handler: ConnectionStateHandler): () => void {
    this.connectionStateHandlers.add(handler);
    return () => {
      this.connectionStateHandlers.delete(handler);
    };
  }

  /**
   * @deprecated Use onConnectionState instead
   */
  onConnectionStateChange(handler: (connected: boolean) => void): () => void {
    const wrappedHandler: ConnectionStateHandler = (state) => {
      handler(state === 'connected');
    };
    this.connectionStateHandlers.add(wrappedHandler);
    return () => {
      this.connectionStateHandlers.delete(wrappedHandler);
    };
  }

  /**
   * Get current connection state
   */
  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * @deprecated Use getConnectionState instead
   */
  isConnected(): boolean {
    return this.connectionState === 'connected';
  }

  private handleOpen(): void {
    console.log('WebSocket connected');
    this.reconnectAttempts = 0;
    this.setConnectionState('connected');
    this.startPing();
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const raw = JSON.parse(event.data);
      const message = parseWebSocketMessage(raw);
      if (!message) {
        console.warn('Discarding invalid WebSocket message:', event.data);
        return;
      }
      this.messageHandlers.forEach(handler => {
        try {
          handler(message);
        } catch (error) {
          console.error('Error in message handler:', error);
        }
      });
    } catch (error) {
      console.warn('Failed to parse WebSocket message:', event.data);
    }
  }

  private handleError(event: Event): void {
    console.error('WebSocket error:', event);
  }

  private handleClose(_event: CloseEvent): void {
    this.ws = null;
    this.stopPing();

    if (!this.isIntentionallyClosed) {
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached, activating fallback');
      this.setConnectionState('fallback');
      return;
    }

    this.setConnectionState('reconnecting');

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    const delay = this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts);
    console.log(`Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);

    this.reconnectTimer = setTimeout(async () => {
      this.reconnectAttempts++;
      // Fetch a fresh token on reconnect
      try {
        const session = await fetchAuthSession();
        const freshToken = session.tokens?.idToken?.toString();
        if (freshToken) {
          this.authToken = freshToken;
        }
      } catch {
        // Use existing token as fallback
      }
      if (this.authToken) {
        this.connect(this.authToken);
      }
    }, delay);
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      this.send({ action: 'ping' });
    }, WebSocketManager.PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private setConnectionState(state: ConnectionState): void {
    if (this.connectionState === state) return;
    this.connectionState = state;
    this.connectionStateHandlers.forEach(handler => {
      try {
        handler(state);
      } catch (error) {
        console.error('Error in connection state handler:', error);
      }
    });
  }
}

// Singleton instance
export const webSocketManager = new WebSocketManager();
